import { recordAnalyticsEvent } from "../app/modules/Auth/_redux/authCrud";

/**
 * Website analytics for the public pages (same approach as Tamam's analyticsBeacon).
 *
 * Feeds the "Visited" and "App clicks" cards of the admin dashboard, and the anonymous visitor id
 * that signup saves on the company (acquisitionVisitorId) so the conversion funnel can attribute an
 * account to the visit that produced it.
 *
 * Tracking must never break or delay the page: every path is wrapped and failures are swallowed.
 */

// Existing keys - kept so browsers that already have a visitor id keep it
const VISITOR_KEY = "visitorId";
const LAST_VISIT_KEY = "lastVisitDate";

const uuidv4 = () => {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  // crypto.randomUUID needs a secure context and a recent browser
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const getOrCreateVisitorId = () => {
  try {
    let id = window.localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = uuidv4();
      window.localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch (e) {
    return uuidv4();
  }
};

// UTC day, the same day the backend stores the visit under
const today = () => new Date().toISOString().split("T")[0];

const readLastVisit = () => {
  try {
    return window.localStorage.getItem(LAST_VISIT_KEY);
  } catch (e) {
    return null;
  }
};

let visitInFlight = null;

/**
 * Record today's visit. The backend keeps one visit per visitor per UTC day; the browser also
 * remembers the day it last recorded one, so navigating between public pages does not resend it.
 * The day is only remembered once the backend accepted the visit, so a failed call is retried on
 * the next page view.
 */
export const trackVisit = () => {
  try {
    const day = today();
    if (readLastVisit() === day) return Promise.resolve();
    if (visitInFlight) return visitInFlight;

    visitInFlight = recordAnalyticsEvent({ type: "visit", platform: "web", visitorId: getOrCreateVisitorId() })
      .then(() => {
        try {
          window.localStorage.setItem(LAST_VISIT_KEY, day);
        } catch (e) {
          // storage unavailable - the backend still de-duplicates the day
        }
      })
      .catch(() => {})
      .finally(() => {
        visitInFlight = null;
      });
    return visitInFlight;
  } catch (e) {
    return Promise.resolve();
  }
};

/** A click on the mobile app download link. Every click is recorded. */
export const trackAppClick = () => {
  try {
    return recordAnalyticsEvent({ type: "app_click", platform: "web", visitorId: getOrCreateVisitorId() }).catch(() => {});
  } catch (e) {
    return Promise.resolve();
  }
};

/** Visitor id for signup - the same id the visit was recorded with. */
export const getVisitorId = () => getOrCreateVisitorId();
